import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Quote = {
  id: string;
  created_at: string;
  email: string;
  estimated_price: number;
  status: string;
  quote_details: any;
};

const Quotes = () => {
  const { isAdmin, loading, signOut } = useAdminAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchQuotes = async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (data) {
        setQuotes(data);
      }
    };

    if (isAdmin) {
      fetchQuotes();
    }
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-4">You don't have admin privileges.</p>
          <Button asChild>
            <Link to="/admin/login">Return to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <img src="/assets/logo.png" alt="OneWeekMVPs" className="h-8" />
              <nav className="flex gap-6">
                <Link to="/admin/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Dashboard
                </Link>
                <Link to="/admin/quotes" className="text-sm font-medium text-foreground">
                  Quotes
                </Link>
                <Link to="/admin/offers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Offers
                </Link>
              </nav>
            </div>
            <Button variant="outline" onClick={signOut}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl mb-2">Calculator Quotes</h1>
          <p className="text-muted-foreground">Review and manage price quotes from the calculator</p>
        </div>

        <Card className="border-accent/20">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell>{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{quote.email}</TableCell>
                  <TableCell>${quote.estimated_price.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{quote.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSelectedQuote(quote);
                        setDialogOpen(true);
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </main>

      {/* Quote Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Contact Information</h3>
                <p className="text-sm">Email: {selectedQuote.email}</p>
                <p className="text-sm">Date: {new Date(selectedQuote.created_at).toLocaleString()}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Project Details</h3>
                <div className="text-sm space-y-1">
                  {selectedQuote.quote_details && typeof selectedQuote.quote_details === 'object' && (
                    <>
                      {Object.entries(selectedQuote.quote_details).map(([key, value]) => (
                        <p key={key}>
                          <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Estimated Price</h3>
                <p className="text-2xl font-heading">${selectedQuote.estimated_price.toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quotes;
